import React, { useState, useRef, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, Platform, Animated, Easing, TextInput, Image, Modal, ScrollView, ActivityIndicator } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { isMobile, isTablet } from '../utils/responsive';
import NavigationHeader from '../components/NavigationHeader';
import { BlurView } from 'expo-blur';
import { GlassBackgroundGradient } from '../components/Gradients';
import { ScreenBackground } from '../components/ThemeGradients';
import AutocompleteDropdown from '../components/AutocompleteDropdown';
import MARKET_MAPPING from '../data/marketMapping.json';
import { useNavigation } from '../context/NavigationContext';
import { useTicker } from '../context/TickerContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import LegBetCard from '../components/LegBetCard';
import { useAuth } from '../context/AuthContext';
import GradientButton from '../components/GradientButton';
import { Ionicons } from '@expo/vector-icons';
import { sportsData, getTeams, getLeagues, getMarkets, getApiLeague, getSportCode, SPORTSBOOKS, MARKET_CATEGORIES, TIME_FILTERS, ALL_SPORTSBOOKS as ALL_SPORTSBOOKS_DATA } from '../data/sportsData';

// Pre-compute full cross-sport lists at module load so initial renders are never empty.
// These are updated only when sportsData changes (i.e. on app deploys).
const ALL_STATIC_TEAMS = (() => {
    const s = new Set();
    Object.keys(sportsData).forEach(sport => getTeams(sport, null).forEach(t => s.add(t)));
    return [...s].sort();
})();

const ALL_STATIC_LEAGUES = (() => {
    const s = new Set();
    Object.keys(sportsData).forEach(sport => getLeagues(sport).forEach(l => s.add(l)));
    return [...s].sort();
})();
import { db } from '../firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';
import { styles } from './sameGameParlayStyles';


// Comprehensive sportsbooks list - matches API sportsbooks
const ALL_SPORTSBOOKS = [
    // Tier 1: Most Popular US Legal
    { name: 'DraftKings', tier: 1, popular: true },
    { name: 'FanDuel', tier: 1, popular: true },
    { name: 'BetMGM', tier: 1, popular: true },
    { name: 'Caesars', tier: 1, popular: true },
    { name: 'ESPNBet', tier: 1, popular: true },
    { name: 'Fanatics', tier: 1, popular: true },
    { name: 'Bet365', tier: 1, popular: true },
    // Tier 2: Well-Known US Legal
    { name: 'BetRivers', tier: 2 },
    { name: 'PointsBet', tier: 2 },
    { name: 'Hard Rock', tier: 2 },
    { name: 'WynnBET', tier: 2 },
    { name: 'Unibet', tier: 2 },
    { name: 'Betfred', tier: 2 },
    { name: 'SuperBook', tier: 2 },
    { name: 'Tipico', tier: 2 },
    { name: 'BetParx', tier: 2 },
    { name: 'Borgata', tier: 2 },
    { name: 'Golden Nugget', tier: 2 },
    { name: 'Bally Bet', tier: 2 },
    { name: 'Circa Sports', tier: 2 },
    { name: 'Station Casinos', tier: 2 },
    { name: 'Betway', tier: 2 },
    { name: 'SI Sportsbook', tier: 2 },
    // Tier 3: Offshore / Sharp / International
    { name: 'Bovada', tier: 3 },
    { name: 'BetOnline', tier: 3 },
    { name: 'MyBookie', tier: 3 },
    { name: 'BetUS', tier: 3 },
    { name: 'Pinnacle', tier: 3 },
    { name: 'Bookmaker', tier: 3 },
    { name: 'Heritage Sports', tier: 3 },
    { name: 'BetAnySports', tier: 3 },
    { name: 'Jazz Sports', tier: 3 },
    { name: 'Sportsbetting.ag', tier: 3 },
    { name: 'GTBets', tier: 3 },
    { name: 'Everygame', tier: 3 },
    { name: 'Xbet', tier: 3 },
    { name: 'BetNow', tier: 3 },
    { name: 'Nitrogen', tier: 3 },
    { name: '5Dimes', tier: 3 },
    { name: 'Intertops', tier: 3 },
    // Tier 4: DFS / Props / Alternative
    { name: 'PrizePicks', tier: 4 },
    { name: 'Underdog', tier: 4 },
    { name: 'Sleeper', tier: 4 },
    { name: 'Fliff', tier: 4 },
    { name: 'Betr', tier: 4 },
    { name: 'Novig', tier: 4 },
    { name: 'ProphetX', tier: 4 },
    { name: 'Thrive Fantasy', tier: 4 },
    { name: 'Monkey Knife Fight', tier: 4 },
    { name: 'Dabble', tier: 4 },
];

// Boost percentage options
const BOOST_OPTIONS = [
    { value: '0', label: 'No Boost' },
    { value: '5', label: '5%' },
    { value: '10', label: '10%' },
    { value: '15', label: '15%' },
    { value: '20', label: '20%' },
    { value: '25', label: '25%' },
    { value: '30', label: '30%' },
    { value: '35', label: '35%' },
    { value: '40', label: '40%' },
    { value: '45', label: '45%' },
    { value: '50', label: '50%' },
    { value: '75', label: '75%' },
    { value: '100', label: '100%' },
];

// Get functions base URL
const getFunctionsBaseUrl = () => {
    if (Platform.OS === 'web') {
        const isLocalDev = window.location.hostname === 'localhost' || 
                          window.location.hostname === '127.0.0.1' ||
                          window.location.port === '19006';
        return isLocalDev 
            ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
            : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
    }
    return 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
};

const SameGameParlayScreen = () => {
    const { navigateTo } = useNavigation();
    const { userProfile } = useAuth();
    const { isTickerEnabled, totalHeaderHeight } = useTicker();
    const { addBackgroundQuery, canAddBackgroundQuery } = useBackgroundQuery();
    
    // Check if background query mode is enabled
    const backgroundQueryMode = userProfile?.preferences?.backgroundQueryMode === true;
    
    // Core state
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    
    // Bet Type state
    const [betType, setBetType] = useState('Straight');
    
    // Filter state
    const [selectedSport, setSelectedSport] = useState('');
    const [selectedLeague, setSelectedLeague] = useState('');
    const [selectedTeam, setSelectedTeam] = useState('');
    const [selectedPlayer, setSelectedPlayer] = useState('');
    const [selectedMarket, setSelectedMarket] = useState('');
    const [selectedMarketCategory, setSelectedMarketCategory] = useState('');
    const [selectedSportsbooks, setSelectedSportsbooks] = useState([]);
    const [selectedTimeFilter, setSelectedTimeFilter] = useState(null);
    const [lineValue, setLineValue] = useState('');
    const [lineDirection, setLineDirection] = useState('');
    const [boostPercent, setBoostPercent] = useState('0'); // Profit boost percentage
    const [showSportsbooksDropdown, setShowSportsbooksDropdown] = useState(false);
    
    // Cached data for dropdowns (fetched from backend)
    const [cachedLeagues, setCachedLeagues] = useState([]);
    const [cachedTeams, setCachedTeams] = useState([]);
    const [cachedFixtures, setCachedFixtures] = useState([]);
    const [cachedMarkets, setCachedMarkets] = useState([]);
    const [loadingCacheData, setLoadingCacheData] = useState(false);
    
    // Player options (fetched when league/team is selected)
    const [playerOptions, setPlayerOptions] = useState([]);
    const [loadingPlayers, setLoadingPlayers] = useState(false);
    
    // Fixture options loaded on-demand per league
    const [loadingFixtures, setLoadingFixtures] = useState(false);
    
    // Comprehensive market data — bundled from MARKET_SPORT_MAPPING.json, always available
    const allMarketsData = MARKET_MAPPING.sportToMarkets || {};
    
    // Fixtures state (for SGP mode)
    const [selectedFixture, setSelectedFixture] = useState(null);
    
    // Multi-query support
    const [queries, setQueries] = useState([]); // Array of query objects
    
    // SGP Legs state
    const [legs, setLegs] = useState([]);
    
    // Coming soon state
    const { hasComingSoonAccess } = useNavigation();
    const [showComingSoon, setShowComingSoon] = useState(false);
    const [sgpComingSoonEnabled, setSgpComingSoonEnabled] = useState(false);
    
    // Animation values
    const pageOpacity = useRef(new Animated.Value(0)).current;
    const pageTranslateY = useRef(new Animated.Value(20)).current;
    
    // Load SGP Coming Soon setting from Firestore
    useEffect(() => {
        const ref = doc(db, 'site_maintenance', 'global');
        const unsub = onSnapshot(ref, (snap) => {
            const d = snap.data() || {};
            const enabled = !!d.sgpComingSoonEnabled;
            setSgpComingSoonEnabled(enabled);
            setShowComingSoon(enabled && !hasComingSoonAccess);
        });
        return () => unsub();
    }, [hasComingSoonAccess]);
    
    // Page load animation
    useEffect(() => {
        Animated.parallel([
            Animated.timing(pageOpacity, { toValue: 1, duration: 800, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
            Animated.timing(pageTranslateY, { toValue: 0, duration: 800, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
        ]).start();
    }, []);

    // Fetch available data from cache on mount
    useEffect(() => {
        fetchCachedData();
    }, []);

    // Fetch players when sport/league/team selection changes.
    // - Nothing selected: load a broad cross-sport list so the dropdown is never empty
    // - Sport only: load the sport's primary league (first in the list) to seed the dropdown
    // - League set: load the specific league roster
    // - Team set: narrow to that team via keyword search on the team's last word
    useEffect(() => {
        const PROD_URL = 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
        if (!selectedSport) {
            // No sport — load a broad all-sports list so the dropdown is pre-populated
            let cancelled = false;
            setLoadingPlayers(true);
            fetch(`${PROD_URL}/getPlayersCache?limit=3334`)
                .then(r => r.json())
                .then(data => {
                    if (!cancelled && data.players) {
                        const names = [...new Set(data.players.map(p => p.name).filter(Boolean))].sort();
                        setPlayerOptions(names);
                    }
                })
                .catch(() => {})
                .finally(() => { if (!cancelled) setLoadingPlayers(false); });
            return () => { cancelled = true; };
        }
        let cancelled = false;
        const fetchPlayers = async () => {
            setLoadingPlayers(true);
            try {
                // Always use production — player data lives in prod Firestore, not local emulator
                const PROD_URL = 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';

                // Resolve which league to query
                const effectiveLeague = selectedLeague ||
                    sportsData[selectedSport]?.leagues?.[0]; // fallback: sport's first/primary league
                if (!effectiveLeague) {
                    if (!cancelled) setLoadingPlayers(false);
                    return;
                }
                const apiLeague = getApiLeague(selectedSport, effectiveLeague);

                // When team is selected, use the last word (team mascot/nickname) which is the most
                // reliable substring match against stored team names in either direction
                let url;
                if (selectedTeam) {
                    const teamKeyword = selectedTeam.trim().split(/\s+/).pop();
                    url = `${PROD_URL}/getPlayersCache?league=${apiLeague}&search=${encodeURIComponent(teamKeyword)}&limit=150`;
                } else {
                    url = `${PROD_URL}/getPlayersCache?league=${apiLeague}&limit=500`;
                }

                const response = await fetch(url);
                const data = await response.json();
                if (!cancelled && data.players && Array.isArray(data.players)) {
                    const names = [...new Set(data.players.map(p => p.name).filter(Boolean))].sort();
                    setPlayerOptions(names);
                }
            } catch (e) {
                console.warn('[Optimizer] Could not load players:', e.message);
            } finally {
                if (!cancelled) setLoadingPlayers(false);
            }
        };
        fetchPlayers();
        return () => { cancelled = true; };
    }, [selectedLeague, selectedSport, selectedTeam]);
    useEffect(() => {
        if (!selectedLeague || !selectedSport) {
            setCachedFixtures([]);
            return;
        }
        let cancelled = false;
        const fetchLeagueFixtures = async () => {
            setLoadingFixtures(true);
            try {
                const base = getFunctionsBaseUrl();
                const apiLeague = getApiLeague(selectedSport, selectedLeague);
                // Include live games so today's active games appear
                const url = `${base}/getAllCachedFixtures?league=${apiLeague}&include_live=true&limit=50`;
                const response = await fetch(url);
                if (!response.ok) {
                    console.warn('[Optimizer] Fixture fetch returned', response.status);
                    return;
                }
                const data = await response.json();
                if (!cancelled && data.fixtures && Array.isArray(data.fixtures)) {
                    const fixtures = data.fixtures.map(f => ({
                        id: f.id,
                        label: `${f.away_team} @ ${f.home_team}`,
                        home_team: f.home_team,
                        away_team: f.away_team,
                        start_date: f.start_date,
                        league: f.league,
                        total_markets: f.total_markets,
                    }));
                    setCachedFixtures(fixtures);
                    // Seed team list from live fixture data too
                    const teams = new Set();
                    data.fixtures.forEach(f => {
                        if (f.home_team) teams.add(f.home_team);
                        if (f.away_team) teams.add(f.away_team);
                    });
                    if (teams.size > 0) setCachedTeams([...teams].sort());
                }
            } catch (e) {
                console.warn('[Optimizer] Could not load fixtures:', e.message);
            } finally {
                if (!cancelled) setLoadingFixtures(false);
            }
        };
        fetchLeagueFixtures();
        return () => { cancelled = true; };
    }, [selectedLeague, selectedSport, selectedTeam]);

    // Debounced player name search — only when neither sport nor league is selected,
    // and the user has typed enough to be searching for a specific player.
    // (When sport/league is set, the main effect above handles the roster.)
    useEffect(() => {
        if (selectedLeague || selectedSport) return; // handled by the main effect above
        if (!selectedPlayer || selectedPlayer.length < 2) return; // don't clear — keep the broad list
        let cancelled = false;
        const timer = setTimeout(async () => {
            setLoadingPlayers(true);
            try {
                // Always use production for player data — Firestore players live in prod, not local emulator
                const PROD_URL = 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
                const url = `${PROD_URL}/getPlayersCache?search=${encodeURIComponent(selectedPlayer)}&limit=30`;
                const response = await fetch(url);
                const data = await response.json();
                if (!cancelled && data.players && Array.isArray(data.players)) {
                    const names = [...new Set(data.players.map(p => p.name).filter(Boolean))].sort();
                    setPlayerOptions(names);
                }
            } catch (e) {
                // player field still works as free text
            } finally {
                if (!cancelled) setLoadingPlayers(false);
            }
        }, 400);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [selectedPlayer, selectedLeague]);

    // Fetch cached leagues/teams from backend (lightweight call)
    const fetchCachedData = async () => {
        setLoadingCacheData(true);
        try {
            const base = getFunctionsBaseUrl();
            // Fetch a smaller set just to seed league/team options — 100 fixtures, recent sports
            const response = await fetch(`${base}/getAllCachedFixtures?limit=50`);
            if (!response.ok) {
                console.warn('[Optimizer] getAllCachedFixtures returned', response.status, '— using static data');
                setLoadingCacheData(false);
                return;
            }
            const data = await response.json();
            
            if (data.fixtures && Array.isArray(data.fixtures)) {
                const leagues = [...new Set(data.fixtures.map(f => f.league).filter(Boolean))];
                setCachedLeagues(leagues.map(l => l.toUpperCase()));
    
                const teams = new Set();
                data.fixtures.forEach(f => {
                    if (f.home_team) teams.add(f.home_team);
                    if (f.away_team) teams.add(f.away_team);
                });
                setCachedTeams([...teams].sort());
    
                // Don't store fixtures here — we'll load per-league on demand
                const markets = new Set();
                data.fixtures.forEach(f => {
                    if (f.markets && typeof f.markets === 'object') {
                        Object.keys(f.markets).forEach(m => markets.add(m));
                    }
                });
                if (markets.size > 0) setCachedMarkets([...markets].sort());
            }
        } catch (error) {
            console.warn('[Optimizer] Error fetching cache data (will use static data):', error.message);
        } finally {
            setLoadingCacheData(false);
        }
    };

    // Get filtered options - combine static data with cached data
    const getLeagueOptions = () => {
        if (!selectedSport) {
            // No sport selected — show ALL leagues from ALL sports (pre-computed + any cached from API)
            return [...new Set([...ALL_STATIC_LEAGUES, ...cachedLeagues])].sort();
        }
        const staticLeagues = getLeagues(selectedSport);
        return [...new Set([...staticLeagues, ...cachedLeagues])].sort();
    };
    
    const getTeamOptions = () => {
        if (selectedFixture) {
            return [selectedFixture.home_team, selectedFixture.away_team].filter(Boolean);
        }

        // Sport + League: prefer live fixture data, fall back to static
        if (selectedSport && selectedLeague) {
            if (cachedFixtures.length > 0) {
                const teamsInLeague = new Set();
                cachedFixtures
                    .filter(f => f.league?.toLowerCase() === selectedLeague.toLowerCase())
                    .forEach(f => {
                        if (f.home_team) teamsInLeague.add(f.home_team);
                        if (f.away_team) teamsInLeague.add(f.away_team);
                    });
                if (teamsInLeague.size > 0) return [...teamsInLeague].sort();
            }
            // Static fallback for this sport + league
            const staticLeagueTeams = getTeams(selectedSport, selectedLeague);
            if (staticLeagueTeams.length > 0) return staticLeagueTeams.sort();
        }

        // Sport only (no league): all teams for that sport across all its leagues
        if (selectedSport) {
            const sportTeams = [...new Set([...getTeams(selectedSport, null), ...cachedTeams])];
            if (sportTeams.length > 0) return sportTeams.sort();
        }

        // No selections: ALL teams from ALL sports (pre-computed at module load)
        const allTeams = new Set([...ALL_STATIC_TEAMS, ...cachedTeams]);
        return [...allTeams].sort();
    };
    
    const getMarketOptions = () => {
        // No sport selected — return ALL markets from ALL sports combined
        if (!selectedSport) {
            if (Object.keys(allMarketsData).length > 0) {
                const all = new Set();
                Object.values(allMarketsData).forEach(mList => mList.forEach(m => all.add(m)));
                cachedMarkets.forEach(m => all.add(m));
                return [...all].sort();
            }
            // Static fallback while backend data loads
            const allStatic = new Set();
            Object.keys(sportsData).forEach(sport => getMarkets(sport, 'all').forEach(m => allStatic.add(m)));
            return [...allStatic].sort();
        }

        // Use comprehensive market data from backend (300+ markets per sport) with static fallback
        const comprehensiveMarkets = allMarketsData[selectedSport] || [];
        const sportMarkets = comprehensiveMarkets.length > 0
            ? comprehensiveMarkets
            : getMarkets(selectedSport, 'all');
        const sportSet = new Set(sportMarkets.map(m => m.toLowerCase()));

        if (selectedMarketCategory && MARKET_CATEGORIES[selectedMarketCategory]) {
            // Keyword-based filtering: match any market from the full sport list whose name
            // contains one of the category's keywords (case-insensitive). This correctly surfaces
            // all 100+ Player Props variants, all quarter/half markets, etc.
            const keywords = MARKET_CATEGORIES[selectedMarketCategory];
            const allSportMarkets = [...new Set([...sportMarkets, ...cachedMarkets])];
            return allSportMarkets.filter(m => {
                const ml = m.toLowerCase();
                return keywords.some(kw => ml.includes(kw));
            }).sort();
        }

        // No category selected: sport-specific markets merged with dynamic cached markets
        if (sportMarkets.length > 0) {
            return [...new Set([...sportMarkets, ...cachedMarkets])].sort();
        }
        return [...cachedMarkets].sort();
    };

    // Reverse lookup: given a league name, find which sport it belongs to
    const findSportForLeague = (league) => {
        if (!league) return null;
        for (const [sport, data] of Object.entries(sportsData)) {
            if (data.leagues?.includes(league)) return sport;
        }
        return null;
    };

    // Reverse lookup: given a team name, find sport + league
    const findSportLeagueForTeam = (team) => {
        if (!team) return null;
        for (const [sport, data] of Object.entries(sportsData)) {
            for (const [league, teams] of Object.entries(data.teams || {})) {
                if (teams.includes(team)) return { sport, league };
            }
        }
        return null;
    };

    // Sport selection: clear league/team/fixture only if they are incompatible with the new sport
    const handleSportSelect = (newSport) => {
        setSelectedSport(newSport);
        if (newSport) {
            const sportLeagues = sportsData[newSport]?.leagues || [];
            if (selectedLeague && !sportLeagues.includes(selectedLeague)) {
                setSelectedLeague('');
                setSelectedTeam('');
                setSelectedFixture(null);
            } else if (selectedTeam) {
                // Check if team belongs to new sport
                const found = findSportLeagueForTeam(selectedTeam);
                if (found && found.sport !== newSport) {
                    setSelectedTeam('');
                    setSelectedFixture(null);
                }
            }
        }
    };

    // League selection: auto-set sport (no useEffect fires), clear team only if incompatible
    const handleLeagueSelect = (newLeague) => {
        if (!newLeague) {
            setSelectedLeague('');
            return;
        }
        const sport = findSportForLeague(newLeague);
        // Set sport first if needed (no cascade effect — useEffects are gone)
        if (sport && sport !== selectedSport) setSelectedSport(sport);
        setSelectedLeague(newLeague);
        // Clear team if it doesn't belong to this league
        if (selectedTeam) {
            const teamsInLeague = sportsData[sport || selectedSport]?.teams?.[newLeague] || [];
            // Only clear if we have team data for this league and team isn't in it
            if (teamsInLeague.length > 0 && !teamsInLeague.includes(selectedTeam)) {
                setSelectedTeam('');
                setSelectedFixture(null);
            }
        }
    };

    // Team selection: auto-set sport+league if unset, clear incompatible fixture
    const handleTeamSelect = (newTeam) => {
        setSelectedTeam(newTeam);
        if (newTeam) {
            const found = findSportLeagueForTeam(newTeam);
            if (found) {
                if (!selectedSport) setSelectedSport(found.sport);
                if (!selectedLeague) setSelectedLeague(found.league);
            }
            // Fixture is team-specific — clear if team changed
            if (selectedFixture &&
                selectedFixture.home_team !== newTeam &&
                selectedFixture.away_team !== newTeam) {
                setSelectedFixture(null);
            }
        }
    };

    // Market selection: auto-set sport if market uniquely identifies one sport
    const handleMarketSelect = (market) => {
        setSelectedMarket(market);
        if (market && !selectedSport) {
            const mSports = MARKET_MAPPING.marketToSports?.[market] || [];
            if (mSports.length === 1) setSelectedSport(mSports[0]);
        }
    };

    const getFixtureOptions = () => {
        // Filter by league if selected
        let fixtures = cachedFixtures;
        if (selectedLeague) {
            fixtures = cachedFixtures.filter(f => 
                f.league?.toLowerCase() === selectedLeague.toLowerCase()
            );
        }
        return fixtures.map(f => f.label).sort();
    };

    // Handle fixture selection
    const handleFixtureSelect = (fixtureLabel) => {
        const fixture = cachedFixtures.find(f => f.label === fixtureLabel);
        setSelectedFixture(fixture);
                setSelectedTeam('');
    };

    // Build query from current filters
    const buildQueryFromFilters = () => {
        const parts = [];
        
        // Always include league/sport so backend can correctly route the query
        if (selectedLeague) parts.push(selectedLeague);
        else if (selectedSport) parts.push(selectedSport);
        
        if (selectedTeam) parts.push(selectedTeam);
        if (selectedPlayer) parts.push(selectedPlayer);
        if (selectedMarket && selectedMarket.toLowerCase() !== 'all') parts.push(selectedMarket);
        if (lineValue && lineDirection) {
            parts.push(`${lineDirection} ${lineValue}`);
        } else if (lineValue) {
            parts.push(lineValue);
        }
        if (selectedTimeFilter) {
            const timeLabel = TIME_FILTERS.find(t => t.value === selectedTimeFilter)?.label;
            if (timeLabel && timeLabel !== 'All Games') parts.push(timeLabel.toLowerCase());
        }
        
        // Append single selected sportsbook so the backend universal filter applies
        if (selectedSportsbooks.length === 1) parts.push(selectedSportsbooks[0]);
        
        return parts.join(' ');
    };

    // Add current selection as a query
    const addQuery = () => {
        const queryText = buildQueryFromFilters();
        if (!queryText) {
            setErrorMsg('Please select at least one filter before adding a query');
            return;
        }
        
        const newQuery = {
            id: Date.now(),
            text: queryText,
            sport: selectedSport,
            league: selectedLeague,
            team: selectedTeam,
            player: selectedPlayer,
            market: selectedMarket,
            line: lineValue,
            direction: lineDirection
        };
        
        setQueries(prev => [...prev, newQuery]);
        
        // Clear current filters for next query
        setSelectedTeam('');
        setSelectedPlayer('');
        setSelectedMarket('');
        setLineValue('');
        setLineDirection('');
        setErrorMsg('');
    };

    // Remove a query
    const removeQuery = (queryId) => {
        setQueries(prev => prev.filter(q => q.id !== queryId));
    };

    // Add leg for SGP mode
    const addLeg = () => {
        if (!selectedMarket && !selectedTeam && !selectedPlayer) {
            setErrorMsg('Please select at least a market, team, or player');
            return;
        }
        
        const legDescription = buildQueryFromFilters();
        const newLeg = {
            id: Date.now(),
            sport: selectedSport,
            league: selectedLeague,
            fixture: selectedFixture?.label,
            team: selectedTeam,
            player: selectedPlayer,
            market: selectedMarket,
            line: lineValue,
            direction: lineDirection,
            description: legDescription
        };
        
        setLegs(prev => [...prev, newLeg]);
        
        // Clear current selections for next leg
        setSelectedTeam('');
        setSelectedPlayer('');
        setSelectedMarket('');
        setLineValue('');
        setLineDirection('');
        setErrorMsg('');
    };
    
    // Remove a leg
    const removeLeg = (legId) => {
        setLegs(prev => prev.filter(leg => leg.id !== legId));
    };
    
    // Clear all filters
    const clearAllFilters = () => {
        setSelectedSport('');
        setSelectedLeague('');
        setSelectedTeam('');
        setSelectedPlayer('');
        setSelectedMarket('');
        setSelectedMarketCategory('');
        setSelectedSportsbooks([]);
        setSelectedTimeFilter(null);
        setLineValue('');
        setLineDirection('');
        setSelectedFixture(null);
        setLegs([]);
        setQueries([]);
        setErrorMsg('');
    };

    // Build final query string
    const getFinalQuery = () => {
        if (betType === 'SGP') {
            if (legs.length > 0) {
                return legs.map(l => l.description).join(' + ') + ' sgp';
            }
            return '';
        } else {
            // For Straight (or null/unset), combine all added queries with AND, plus current filters
            const currentQuery = buildQueryFromFilters();
            const allQueries = [...queries.map(q => q.text)];
            if (currentQuery) {
                allQueries.push(currentQuery);
            }
            return allQueries.join(' AND ') || '';
        }
    };

    // Run the search
    const runSearch = async (forceBackground = false) => {
        const query = getFinalQuery();
        
        if (!query) {
        if (betType === 'SGP') {
                setErrorMsg('Please add at least 1 leg for your Same Game Parlay');
            } else {
                setErrorMsg('Please select at least one filter or add a query');
            }
            return;
        }
        
        console.log('[Optimizer] Running search:', query, 'background:', forceBackground || backgroundQueryMode);
        setErrorMsg('');
        
        // Check if background query mode is enabled
        const useBackground = forceBackground || (backgroundQueryMode && canAddBackgroundQuery);
        
        if (useBackground) {
            // Add to background queue - no page navigation
            await addBackgroundQuery(query, {
                sport: getSportCode(selectedSport),
                league: getApiLeague(selectedSport, selectedLeague),
                team: selectedTeam,
                player: selectedPlayer,
                market: selectedMarket,
                sportsbooks: selectedSportsbooks,
                fromOptimizer: true,
            });
                return;
            }
            
        // Navigate to SgpFlow with the query (foreground mode)
        navigateTo('SgpFlow', { 
            payload: { 
                query, 
                fromOptimizer: true,
                filters: {
                    sport: getSportCode(selectedSport),
                    league: getApiLeague(selectedSport, selectedLeague),
                    team: selectedTeam,
                    player: selectedPlayer,
                    market: selectedMarket,
                    sportsbooks: selectedSportsbooks,
                    timeFilter: selectedTimeFilter
                }
            } 
        });
    };

    // Toggle sportsbook selection
    const toggleSportsbook = (book) => {
        setSelectedSportsbooks(prev => 
            prev.includes(book) 
                ? prev.filter(b => b !== book)
                : [...prev, book]
        );
    };

    const finalQuery = getFinalQuery();

    // Compute user's preferred books for the sportsbooks dropdown
    const myBookIds = userProfile?.preferences?.myBooks || [];
    const myBookNames = myBookIds.map(id => {
        const byId = ALL_SPORTSBOOKS_DATA.find(b => b.id === id);
        if (byId) return byId.name;
        // Fallback: stored as display name
        const byName = ALL_SPORTSBOOKS_DATA.find(b => b.name?.toLowerCase() === (id || '').toLowerCase());
        if (byName) return byName.name;
        return null;
    }).filter(Boolean);

    return (
        <View style={styles.container}>
            <ScreenBackground />
            <View style={[styles.splashContainer, { 
                paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + (isMobile ? 12 : 32)) : (isMobile ? 12 : 16) 
            }]}>
                <NavigationHeader />
                
                {/* Coming Soon Modal */}
                {showComingSoon && (
                    <Modal visible={true} transparent={true} animationType="fade">
                        <View style={styles.comingSoonOverlay}>
                            <BlurView intensity={80} tint="dark" style={styles.comingSoonBlur}>
                                <View style={styles.comingSoonModal}>
                                    <LinearGradient
                                        colors={['rgba(162, 89, 255, 0.1)', 'rgba(162, 89, 255, 0.05)']}
                                        style={styles.comingSoonGradient}
                                    >
                                        <Image 
                                            source={require('../assets/icon.png')} 
                                            style={{ width: 64, height: 64, marginBottom: 24 }}
                                            resizeMode="contain"
                                        />
                                        <Text style={styles.comingSoonTitle}>Coming Soon</Text>
                                        <Text style={styles.comingSoonText}>
                                            The Bet Optimizer is currently in beta.{'\n\n'}
                                            Stay tuned for the official launch!
                                        </Text>
                                        <GradientButton
                                            text="Go Home"
                                            onPress={() => navigateTo('Home')}
                                            style={styles.comingSoonButton}
                                            gradientColors={["#A259FF", "#E0BBFF"]}
                                        />
                                    </LinearGradient>
                                </View>
                            </BlurView>
                        </View>
                    </Modal>
                )}

                <ScrollView 
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                >
                <Animated.View style={[
                    styles.mainContent,
                        { opacity: pageOpacity, transform: [{ translateY: pageTranslateY }] }
                ]}>
                    <BlurView tint="dark" style={styles.mainContentBlur}>
                            {/* Header Section */}
                    <View style={styles.titleSection}>
                                <View style={styles.titleRow}>
                                    <View style={styles.titleTextContainer}>
                            <Text style={styles.title}>
                                            {betType === 'SGP' ? 'PARLAY' : 'ODDS'}{'\n'}OPTIMIZER
                            </Text>
                        <Text style={styles.subtitle}>
                            {betType === 'SGP' 
                                                ? 'Build a Same Game Parlay with granular filters'
                                                : 'Search for the best odds with precise filters'
                            }
                        </Text>
                    </View>
                                    <View style={styles.searchTypeContainer}>
                                        <AutocompleteDropdown
                                            label="Search Type"
                                            value={betType}
                                            placeholder="Select Type"
                                            options={['Straight', 'SGP']}
                                            onValueChange={(v) => {
                                                setBetType(v);
                                                setLegs([]);
                                                setQueries([]);
                                            }}
                                            zIndex={4500}
                                        />
                                </View>
                            </View>

                                {/* Cache status indicator */}
                                {loadingCacheData ? (
                                    <View style={styles.cacheStatus}>
                                        <ActivityIndicator size="small" color="#8b5cf6" />
                                        <Text style={styles.cacheStatusText}>Loading data...</Text>
                                        </View>
                                ) : cachedFixtures.length > 0 && (
                                    <View style={styles.cacheStatus}>
                                        <Ionicons name="checkmark-circle" size={16} color="#22c55e" />
                                        <Text style={styles.cacheStatusText}>
                                            ✓ Loaded from cache
                                                            </Text>
                                                                </View>
                                                            )}
                                                        </View>

                            {/* Main Filters Section */}
                            <View style={[styles.filtersSection, { zIndex: 1000, position: 'relative' }]}>
                                <View style={[styles.filtersRow, { zIndex: 500, position: 'relative' }]}>
                                    {/* Left Column */}
                            <View style={[styles.filtersColumn, { zIndex: 500 }]}>
                                <AutocompleteDropdown
                                    label="Sport"
                                    value={selectedSport}
                                            placeholder="Select sport..."
                                    options={Object.keys(sportsData).sort()}
                                    onValueChange={handleSportSelect}
                                            zIndex={300}
                                    />
                                <AutocompleteDropdown
                                    label="League"
                                    value={selectedLeague}
                                            placeholder={cachedLeagues.length > 0 ? `${cachedLeagues.length} available...` : "Select league..."}
                                    options={getLeagueOptions()}
                                    onValueChange={handleLeagueSelect}
                                            zIndex={290}
                                />
                                
                                        {/* Fixture selector for SGP mode */}
                                {betType === 'SGP' && (
                                        <AutocompleteDropdown
                                                label="Game/Fixture"
                                                value={selectedFixture?.label || ''}
                                                placeholder={loadingFixtures ? 'Loading games...' : selectedLeague ? `${getFixtureOptions().length} games available...` : 'Select league first...'}
                                                options={getFixtureOptions()}
                                                onValueChange={handleFixtureSelect}
                                                zIndex={280}
                                        />
                                )}
                                
                                <AutocompleteDropdown
                                    label="Team"
                                    value={selectedTeam}
                                            placeholder={loadingFixtures ? 'Loading teams...' : `${getTeamOptions().length} teams available...`}
                                    options={getTeamOptions()}
                                    onValueChange={handleTeamSelect}
                                            zIndex={270}
                                />
                                        
                                        <AutocompleteDropdown
                                            label={loadingPlayers ? 'Player (loading...)' : playerOptions.length > 0 ? `Player (${playerOptions.length} available)` : 'Player (optional)'}
                                            value={selectedPlayer}
                                            placeholder="Type or search player..."
                                            options={playerOptions}
                                            onValueChange={(v) => {
                                                setSelectedPlayer(v);
                                                // Auto-switch category away from game-line types when player is selected
                                                if (v && (selectedMarketCategory === 'Game Lines' || selectedMarketCategory === 'Quarter / Half' || selectedMarketCategory === 'Team Props')) {
                                                    setSelectedMarketCategory('Player Props');
                                                }
                                            }}
                                            zIndex={265}
                                            allowFreeText={true}
                                        />
                            </View>

                                    {/* Right Column */}
                            <View style={[styles.filtersColumn, { zIndex: 400 }]}>
                                <AutocompleteDropdown
                                            label="Market Category"
                                            value={selectedMarketCategory}
                                            placeholder="All categories..."
                                            options={Object.keys(MARKET_CATEGORIES).filter(c => {
                                                // When a player is selected, only player-relevant categories make sense
                                                if (selectedPlayer) return c === 'All Markets' || c === 'Player Props';
                                                return true;
                                            })}
                                            onValueChange={(v) => {
                                                setSelectedMarketCategory(v);
                                                setSelectedMarket('');
                                            }}
                                            zIndex={260}
                                />
                                        
                                <AutocompleteDropdown
                                            label="Market"
                                            value={selectedMarket}
                                            placeholder={`${getMarketOptions().length} markets available...`}
                                            options={getMarketOptions()}
                                            onValueChange={handleMarketSelect}
                                            zIndex={250}
                                        />
                                        
                                        <View style={[styles.lineRow, { zIndex: 240 }]}>
                                            <View style={styles.lineValueContainer}>
                                                <Text style={styles.inputLabel}>Line Value</Text>
                                                <View style={styles.textInputWrap}>
                                                    <TextInput
                                                        placeholder="e.g., 25.5"
                                                        placeholderTextColor="rgba(255,255,255,0.4)"
                                                        style={styles.textInputField}
                                                        value={lineValue}
                                                        onChangeText={setLineValue}
                                                        keyboardType="numeric"
                                                    />
                                                </View>
                                            </View>
                                            <View style={styles.directionContainer}>
                                <AutocompleteDropdown
                                                    label="Direction"
                                                    value={lineDirection}
                                                    placeholder="O/U"
                                                    options={['Over', 'Under']}
                                                    onValueChange={setLineDirection}
                                                    zIndex={240}
                                />
                                            </View>
                                        </View>
                                        
                                <AutocompleteDropdown
                                            label="Time Filter"
                                            value={TIME_FILTERS.find(t => t.value === selectedTimeFilter)?.label || ''}
                                            placeholder="All games..."
                                            options={TIME_FILTERS.map(t => t.label)}
                                            onValueChange={(label) => {
                                                const filter = TIME_FILTERS.find(t => t.label === label);
                                                setSelectedTimeFilter(filter?.value || null);
                                            }}
                                            zIndex={230}
                                />
                            </View>
                        </View>

                                {/* Boost % and Sportsbooks Row */}
                                <View style={[styles.filtersRow, { marginTop: 8, zIndex: 100, position: 'relative' }]}>
                                    {/* Boost Percentage Dropdown — Beta (coming soon) */}
                                    <View style={{ flex: isMobile ? 1 : 0.4, minWidth: isMobile ? '100%' : 150, zIndex: 90, opacity: 0.4 }}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                            <Text style={[styles.inputLabel, { marginBottom: 0 }]}>Profit Boost %</Text>
                                            <View style={{ backgroundColor: 'rgba(162,89,255,0.3)', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2, borderWidth: 1, borderColor: 'rgba(162,89,255,0.6)' }}>
                                                <Text style={{ color: '#c084fc', fontSize: 10, fontWeight: '700', letterSpacing: 0.5 }}>BETA</Text>
                                            </View>
                                        </View>
                                        <AutocompleteDropdown
                                            label=""
                                            value={BOOST_OPTIONS.find(b => b.value === boostPercent)?.label || 'No Boost'}
                                            placeholder="No Boost"
                                            options={BOOST_OPTIONS.map(b => b.label)}
                                            onValueChange={() => {}}
                                            zIndex={90}
                                            disabled={true}
                                        />
                                    </View>
                                    
                                    {/* Sportsbooks Multi-Select Dropdown */}
                                    <View style={{ flex: 1, position: 'relative', zIndex: showSportsbooksDropdown ? 99999 : 80 }}>
                                        <Text style={styles.inputLabel}>
                                            Sportsbooks ({selectedSportsbooks.length} selected)
                                        </Text>
                                        <TouchableOpacity
                                            style={styles.sportsbooksSelector}
                                            onPress={() => setShowSportsbooksDropdown(!showSportsbooksDropdown)}
                                        >
                                            <Text style={styles.sportsbooksSelectorText} numberOfLines={1}>
                                                {selectedSportsbooks.length === 0 
                                                    ? 'All Sportsbooks (click to filter)' 
                                                    : selectedSportsbooks.slice(0, 3).join(', ') + (selectedSportsbooks.length > 3 ? ` +${selectedSportsbooks.length - 3} more` : '')
                                                }
                                            </Text>
                                            <Text style={styles.sportsbooksArrow}>{showSportsbooksDropdown ? '▲' : '▼'}</Text>
                                        </TouchableOpacity>
                                        
                                        {showSportsbooksDropdown && (
                                            <View style={styles.sportsbooksDropdown}>
                                                <View style={styles.sportsbooksDropdownHeader}>
                                                    {myBookNames.length > 0 && (
                                                        <TouchableOpacity
                                                            onPress={() => setSelectedSportsbooks(myBookNames)}
                                                            style={[styles.sportsbooksQuickAction, { backgroundColor: 'rgba(162,89,255,0.2)' }]}
                                                        >
                                                            <Text style={[styles.sportsbooksQuickActionText, { color: '#c084fc' }]}>⭐ My Books</Text>
                                                        </TouchableOpacity>
                                                    )}
                                                    <TouchableOpacity 
                                                        onPress={() => setSelectedSportsbooks(ALL_SPORTSBOOKS.filter(b => b.popular).map(b => b.name))} 
                                                        style={styles.sportsbooksQuickAction}
                                                    >
                                                        <Text style={styles.sportsbooksQuickActionText}>Select Popular</Text>
                                                    </TouchableOpacity>
                                                    <TouchableOpacity 
                                                        onPress={() => setSelectedSportsbooks([])} 
                                                        style={[styles.sportsbooksQuickAction, { backgroundColor: 'rgba(248,113,113,0.2)' }]}
                                                    >
                                                        <Text style={[styles.sportsbooksQuickActionText, { color: '#f87171' }]}>Clear</Text>
                                                    </TouchableOpacity>
                                                    <TouchableOpacity onPress={() => setShowSportsbooksDropdown(false)} style={styles.sportsbooksCloseBtn}>
                                                        <Text style={styles.sportsbooksCloseBtnText}>Done ✓</Text>
                                                    </TouchableOpacity>
                                                </View>
                                                
                                                <ScrollView style={styles.sportsbooksList} nestedScrollEnabled>
                                                    {/* My Books — pinned to top if user has preferences set */}
                                                    {myBookNames.length > 0 && (
                                                        <>
                                                            <Text style={[styles.sportsbooksTierLabel, { color: '#c084fc' }]}>⭐ My Books</Text>
                                                            <View style={styles.sportsbooksGridDropdown}>
                                                                {myBookNames.map((name) => (
                                                                    <TouchableOpacity
                                                                        key={`mybook_${name}`}
                                                                        style={[
                                                                            styles.sportsbookChip,
                                                                            styles.sportsbookChipMyBook,
                                                                            selectedSportsbooks.includes(name) && styles.sportsbookChipSelected
                                                                        ]}
                                                                        onPress={() => toggleSportsbook(name)}
                                                                    >
                                                                        <Text style={[
                                                                            styles.sportsbookChipText,
                                                                            selectedSportsbooks.includes(name) && styles.sportsbookChipTextSelected
                                                                        ]}>
                                                                            {name}
                                                                        </Text>
                                                                    </TouchableOpacity>
                                                                ))}
                                                            </View>
                                                        </>
                                                    )}
                                                    
                                                    {/* Popular */}
                                                    <Text style={styles.sportsbooksTierLabel}>⭐ Most Popular</Text>
                                                    <View style={styles.sportsbooksGridDropdown}>
                                                        {ALL_SPORTSBOOKS.filter(b => b.tier === 1).map((book) => (
                                                            <TouchableOpacity
                                                                key={book.name}
                                                                style={[
                                                                    styles.sportsbookChip,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                                ]}
                                                                onPress={() => toggleSportsbook(book.name)}
                                                            >
                                                                <Text style={[
                                                                    styles.sportsbookChipText,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                                ]}>
                                                                    {book.name}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        ))}
                                                    </View>
                                                    
                                                    {/* US Legal */}
                                                    <Text style={styles.sportsbooksTierLabel}>🇺🇸 US Legal</Text>
                                                    <View style={styles.sportsbooksGridDropdown}>
                                                        {ALL_SPORTSBOOKS.filter(b => b.tier === 2).map((book) => (
                                                            <TouchableOpacity
                                                                key={book.name}
                                                                style={[
                                                                    styles.sportsbookChip,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                                ]}
                                                                onPress={() => toggleSportsbook(book.name)}
                                                            >
                                                                <Text style={[
                                                                    styles.sportsbookChipText,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                                ]}>
                                                                    {book.name}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        ))}
                                                    </View>
                                                    
                                                    {/* Offshore/Sharp */}
                                                    <Text style={styles.sportsbooksTierLabel}>🌐 Offshore / Sharp</Text>
                                                    <View style={styles.sportsbooksGridDropdown}>
                                                        {ALL_SPORTSBOOKS.filter(b => b.tier === 3).map((book) => (
                                                            <TouchableOpacity
                                                                key={book.name}
                                                                style={[
                                                                    styles.sportsbookChip,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                                ]}
                                                                onPress={() => toggleSportsbook(book.name)}
                                                            >
                                                                <Text style={[
                                                                    styles.sportsbookChipText,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                                ]}>
                                                                    {book.name}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        ))}
                                                    </View>
                                                    
                                                    {/* DFS / Props */}
                                                    <Text style={styles.sportsbooksTierLabel}>🎲 DFS / Props</Text>
                                                    <View style={styles.sportsbooksGridDropdown}>
                                                        {ALL_SPORTSBOOKS.filter(b => b.tier === 4).map((book) => (
                                                            <TouchableOpacity
                                                                key={book.name}
                                                                style={[
                                                                    styles.sportsbookChip,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipSelected
                                                                ]}
                                                                onPress={() => toggleSportsbook(book.name)}
                                                            >
                                                                <Text style={[
                                                                    styles.sportsbookChipText,
                                                                    selectedSportsbooks.includes(book.name) && styles.sportsbookChipTextSelected
                                                                ]}>
                                                                    {book.name}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        ))}
                                                    </View>
                                                </ScrollView>
                                            </View>
                                        )}
                                    </View>
                                </View>
                                
                                {/* Selected Sportsbooks Summary */}
                                {selectedSportsbooks.length > 0 && (
                                    <View style={styles.selectedSportsbooksSummary}>
                                        <View style={styles.selectedSportsbooksHeader}>
                                            <Text style={styles.selectedSportsbooksLabel}>
                                                🎯 Filtering: {selectedSportsbooks.length} book{selectedSportsbooks.length !== 1 ? 's' : ''}
                                            </Text>
                                            <TouchableOpacity onPress={() => setSelectedSportsbooks([])}>
                                                <Text style={styles.clearSportsbooksBtn}>Clear</Text>
                                            </TouchableOpacity>
                                        </View>
                                        <View style={styles.selectedSportsbooksChips}>
                                            {selectedSportsbooks.map((book) => (
                                                <TouchableOpacity 
                                                    key={book} 
                                                    style={styles.selectedSportsbookChip}
                                                    onPress={() => toggleSportsbook(book)}
                                                >
                                                    <Text style={styles.selectedSportsbookChipText}>{book}</Text>
                                                    <Text style={styles.selectedSportsbookRemove}>×</Text>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                    </View>
                                )}

                                {/* Added Queries Display (Straight mode) */}
                                {betType === 'Straight' && queries.length > 0 && (
                                    <View style={styles.queriesSection}>
                                        <Text style={styles.sectionLabel}>Combined Queries ({queries.length})</Text>
                                        {queries.map((q, index) => (
                                            <View key={q.id} style={styles.queryCard}>
                                                <View style={styles.queryCardContent}>
                                                    <Text style={styles.queryCardNumber}>Query {index + 1}</Text>
                                                    <Text style={styles.queryCardText}>{q.text}</Text>
                                                </View>
                                                <TouchableOpacity 
                                                    style={styles.removeButton}
                                                    onPress={() => removeQuery(q.id)}
                                                >
                                                    <Text style={styles.removeButtonText}>×</Text>
                                                </TouchableOpacity>
                                            </View>
                                        ))}
                                    </View>
                                )}

                                {/* SGP Legs Display */}
                            {betType === 'SGP' && legs.length > 0 && (
                                    <View style={styles.legsSection}>
                                        <Text style={styles.sectionLabel}>Parlay Legs ({legs.length})</Text>
                                    {legs.map((leg, index) => (
                                        <View key={leg.id} style={styles.legCard}>
                                            <View style={styles.legCardContent}>
                                                <Text style={styles.legCardNumber}>Leg {index + 1}</Text>
                                                <Text style={styles.legCardDescription}>{leg.description}</Text>
                                                    {leg.fixture && (
                                                        <Text style={styles.legCardDetails}>{leg.fixture}</Text>
                                                    )}
                                            </View>
                                            <TouchableOpacity 
                                                    style={styles.removeButton}
                                                onPress={() => removeLeg(leg.id)}
                                            >
                                                    <Text style={styles.removeButtonText}>×</Text>
                                            </TouchableOpacity>
                                        </View>
                                    ))}
                                </View>
                            )}
                            
                                {/* Query Preview */}
                                <View style={styles.queryPreview}>
                                    <Text style={styles.queryPreviewLabel}>QUERY PREVIEW:</Text>
                                    <Text style={styles.queryPreviewText}>
                                        {finalQuery || '(Select filters to build your query)'}
                                </Text>
                                </View>

                                {/* Error Message */}
                                {errorMsg && (
                                    <View style={styles.errorContainer}>
                                        <Text style={styles.errorText}>{errorMsg}</Text>
                                    </View>
                            )}
                            
                                {/* Action Buttons */}
                            <View style={styles.actionButtons}>
                                {betType === 'SGP' ? (
                                        <TouchableOpacity 
                                            style={[styles.actionButton, styles.addButton]}
                                            onPress={addLeg}
                                        >
                                            <Ionicons name="add-circle-outline" size={18} color="#fff" />
                                            <Text style={styles.actionButtonText}>Add Leg</Text>
                                        </TouchableOpacity>
                                    ) : (
                                        <TouchableOpacity 
                                            style={[styles.actionButton, styles.addButton]}
                                            onPress={addQuery}
                                        >
                                            <Ionicons name="add-circle-outline" size={18} color="#fff" />
                                            <Text style={styles.actionButtonText}>Add Query</Text>
                                        </TouchableOpacity>
                                        )}
                                    
                                        <TouchableOpacity 
                                            style={[styles.actionButton, styles.clearButton]} 
                                        onPress={clearAllFilters}
                                        >
                                        <Ionicons name="refresh-outline" size={18} color="#fff" />
                                        <Text style={styles.actionButtonText}>Clear All</Text>
                                        </TouchableOpacity>
                                    
                                        <TouchableOpacity 
                                            style={[
                                                styles.actionButton, 
                                                styles.searchButton,
                                            (!finalQuery || loading) && styles.actionButtonDisabled
                                            ]} 
                                        onPress={runSearch}
                                        disabled={!finalQuery || loading}
                                    >
                                        <Ionicons name="search-outline" size={18} color="#fff" />
                                        <Text style={styles.actionButtonText}>
                                            {loading ? 'Searching...' : 'Search'}
                                    </Text>
                                    </TouchableOpacity>
                            </View>
                    </View>
                    </BlurView>
                </Animated.View>
                </ScrollView>
            </View>
        </View>
    );
};


export default SameGameParlayScreen;
